import { useNavigate, useLocation } from 'react-router-dom';
import { useWishlist } from '../../utils/wishlist';

// Inline SVGs rather than emoji: emoji render as platform-specific colour
// glyphs that don't inherit the active-tab colour and look inconsistent
// across Android/iOS/Windows. currentColor lets CSS drive the active state.
function IconHome() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
      <path d="M9.5 21v-6h5v6" />
    </svg>
  );
}

function IconCategories() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </svg>
  );
}

function IconFlame() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2.5c2.5 3.5 1 5 0 6-1.2 1.2-3 2.2-3 4.8a5.4 5.4 0 0 0 3 4.9" />
      <path d="M12 2.5c.8 3 2.4 4 4 6a7 7 0 0 1 1.6 4.4A6.6 6.6 0 0 1 12 21.5a6.6 6.6 0 0 1-6.6-6.6" />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function IconBlogs() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 4H7a2 2 0 0 0-2 2v14l3.2-2.2H19a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
      <path d="M9 9h8M9 12.5h5.5" />
    </svg>
  );
}

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { items } = useWishlist();

  const path = location.pathname;
  const isHome = path === '/';
  const isCategories = path === '/categories';
  const isHotDeals = path === '/hot-deals';
  const isBlogs = path.startsWith('/blog');
  const isSaved = path === '/saved';

  // Real hrefs: shareable, crawlable, middle-clickable, and the browser Back
  // button behaves. The onClick only intercepts plain left-clicks so React
  // Router can handle them client-side.
  function linkProps(to, { active = false, onSamePage } = {}) {
    return {
      href: to,
      className: `bottom-nav-item ${active ? 'active' : ''}`,
      'aria-current': active ? 'page' : undefined,
      onClick(e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        if (active && onSamePage) onSamePage();
        else navigate(to);
      },
    };
  }

  return (
    <nav className="bottom-nav" aria-label="Primary">
      <a {...linkProps('/', {
        active: isHome,
        onSamePage: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
      })}>
        <span className="bottom-nav-icon"><IconHome /></span>
        <span className="bottom-nav-label">Home</span>
      </a>

      <a {...linkProps('/categories', { active: isCategories })}>
        <span className="bottom-nav-icon"><IconCategories /></span>
        <span className="bottom-nav-label">Categories</span>
      </a>

      <a
        {...linkProps('/hot-deals', { active: isHotDeals })}
        className={`bottom-nav-item bottom-nav-item-center ${isHotDeals ? 'active' : ''}`}
      >
        <span className="bottom-nav-center-circle"><IconFlame /></span>
        <span className="bottom-nav-label">Hot Deals</span>
      </a>

      <a {...linkProps('/saved', { active: isSaved })}>
        <span className="bottom-nav-icon bottom-nav-icon-badged">
          <IconHeart />
          {items.length > 0 && (
            <span className="bottom-nav-badge">{items.length > 99 ? '99+' : items.length}</span>
          )}
        </span>
        <span className="bottom-nav-label">Saved</span>
      </a>

      <a {...linkProps('/blogs', { active: isBlogs })}>
        <span className="bottom-nav-icon"><IconBlogs /></span>
        <span className="bottom-nav-label">Blogs</span>
      </a>
    </nav>
  );
}

export default BottomNav;
